import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_fintech-notify-check/artifacts/2hk0onm3_My%20Direct%20debit%20reminder%20app%20logo.png";

export default function CookiePolicy() {
  return (
    <div className="min-h-screen bg-page">
      <header className="glass-header px-4 py-4">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <Link to="/" className="text-text-secondary hover:text-primary transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <img src={LOGO_URL} alt="My Direct Debits" className="h-8 w-8" />
          <span className="font-heading font-semibold text-dark">Cookie Policy</span>
        </div>
      </header>

      <main className="px-4 py-8">
        <div className="max-w-3xl mx-auto card-default p-8">
          <h1 className="font-heading font-bold text-dark text-3xl mb-2">Cookie Policy</h1>
          <p className="text-text-light text-sm mb-8">Last updated: April 2026</p>

          <div className="prose prose-green max-w-none space-y-6 text-text-secondary">
            <section>
              <h2 className="font-heading font-semibold text-dark text-xl mb-3">1. Introduction</h2>
              <p>
                This Cookie Policy explains how My Direct Debits ("we", "us", or "our"), operated by
                Voliciti Group Ventures Ltd, uses cookies and similar technologies when you visit our
                website at <strong>mddreminder.uk</strong> or use our application.
              </p>
              <p className="mt-2">
                We are committed to transparency. We <strong>do not use analytics, advertising, or marketing
                cookies</strong>. We do not track your browsing behaviour across other websites. The only
                technologies we use are essential to making the service work.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-dark text-xl mb-3">2. What Are Cookies?</h2>
              <p>
                Cookies are small text files placed on your device by websites you visit. "Similar technologies"
                include localStorage and sessionStorage, which serve comparable purposes within your browser.
                These are used to remember preferences, maintain sessions, and ensure the website functions correctly.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-dark text-xl mb-3">3. Technologies We Use</h2>
              <p>
                We only use <strong>strictly essential</strong> technologies required for the service to function.
                These cannot be disabled without breaking the application.
              </p>

              <h3 className="font-heading font-semibold text-dark text-lg mt-4 mb-2">3.1 Authentication Token (localStorage)</h3>
              <p>
                When you sign in, we store a secure authentication token (JWT) in your browser's localStorage.
                This keeps you logged in between visits so you don't have to re-enter your credentials each time.
                This token contains no personal data beyond your account identifier and expires automatically.
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li><strong>Technology:</strong> localStorage (similar to cookies)</li>
                <li><strong>Purpose:</strong> Authentication and session management</li>
                <li><strong>Duration:</strong> Until you log out or the token expires (30 days)</li>
                <li><strong>Category:</strong> Strictly essential</li>
              </ul>

              <h3 className="font-heading font-semibold text-dark text-lg mt-4 mb-2">3.2 Cookie Consent Preference (localStorage)</h3>
              <p>
                We store your acknowledgement of this cookie notice so the banner is not shown on every visit.
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li><strong>Technology:</strong> localStorage</li>
                <li><strong>Purpose:</strong> Remember your consent acknowledgement</li>
                <li><strong>Duration:</strong> Persistent until cleared</li>
                <li><strong>Category:</strong> Strictly essential</li>
              </ul>

              <h3 className="font-heading font-semibold text-dark text-lg mt-4 mb-2">3.3 Cloudflare (Third-Party)</h3>
              <p>
                Our website is served through Cloudflare, which may set a cookie (<code>__cf_bm</code>) to
                distinguish between humans and bots. This is essential for protecting the service against
                malicious traffic and denial-of-service attacks.
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li><strong>Cookie:</strong> <code>__cf_bm</code></li>
                <li><strong>Set by:</strong> Cloudflare</li>
                <li><strong>Purpose:</strong> Bot detection and security</li>
                <li><strong>Duration:</strong> 30 minutes</li>
                <li><strong>Category:</strong> Strictly essential (security)</li>
              </ul>

              <h3 className="font-heading font-semibold text-dark text-lg mt-4 mb-2">3.4 Stripe (Third-Party)</h3>
              <p>
                If you choose to upgrade to Premium, you will be redirected to Stripe's secure checkout page.
                Stripe may set its own cookies during the payment process to prevent fraud and process your
                transaction securely. These cookies are set by Stripe on their domain, not by us.
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li><strong>Set by:</strong> Stripe (checkout.stripe.com)</li>
                <li><strong>Purpose:</strong> Payment processing and fraud prevention</li>
                <li><strong>Duration:</strong> Varies (session to short-term)</li>
                <li><strong>Category:</strong> Strictly essential (payment security)</li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-dark text-xl mb-3">4. What We Do NOT Use</h2>
              <p>To be completely clear, we do <strong>not</strong> use:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Google Analytics or any other analytics cookies</li>
                <li>Advertising or marketing cookies</li>
                <li>Social media tracking pixels</li>
                <li>Cross-site tracking technologies</li>
                <li>Any technology that profiles your browsing behaviour</li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-dark text-xl mb-3">5. Managing Cookies</h2>
              <p>
                Since we only use essential technologies, disabling them may prevent the application from
                working correctly. However, you can manage cookies and localStorage through your browser settings:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li><strong>Chrome:</strong> Settings &gt; Privacy and Security &gt; Cookies and other site data</li>
                <li><strong>Firefox:</strong> Settings &gt; Privacy &amp; Security &gt; Cookies and Site Data</li>
                <li><strong>Safari:</strong> Preferences &gt; Privacy &gt; Manage Website Data</li>
                <li><strong>Edge:</strong> Settings &gt; Cookies and site permissions</li>
              </ul>
              <p className="mt-2">
                You can also clear your localStorage data by logging out of the application, which removes
                your authentication token.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-dark text-xl mb-3">6. Changes to This Policy</h2>
              <p>
                We may update this Cookie Policy from time to time. If we introduce any non-essential cookies
                in the future, we will update this policy and request your consent before setting them.
                The "Last updated" date at the top of this page indicates when this policy was last revised.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-dark text-xl mb-3">7. Contact Us</h2>
              <p>If you have any questions about our use of cookies or similar technologies, please contact us:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li><strong>Email:</strong> info@mddreminder.uk</li>
                <li><strong>Phone:</strong> +44 (0) 7367 431859</li>
                <li><strong>Web:</strong> mddreminder.uk</li>
              </ul>
            </section>
          </div>

          <div className="mt-8 pt-6 border-t border-border flex flex-wrap gap-4">
            <Link to="/privacy" className="text-primary font-medium hover:underline text-sm">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-primary font-medium hover:underline text-sm">
              Terms of Service
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
